import {
  createBrowserRouter,
  Navigate,
  RouteObject,
  RouterProvider,
  useRouteError,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import { PlatformWithSidebar } from "./layouts/PlatformWithSidebar";
import { Loader } from "@/components/Loader";
import { SettingsLayout } from "@/pages/Settings/SettingsLayout";

// Deal layout gets its OWN Suspense boundary (below) so a cold/direct
// load onto a deal URL doesn't flash the router-level `fallbackElement`
// full-page spinner before DealLayout's own deal-shaped skeleton appears.
// Using React.lazy + a route-local <Suspense fallback={null}> keeps the
// global spinner from firing for THIS route only — every other route is
// untouched and still uses the router `fallbackElement`.
const DealLayoutLazy = lazy(async () => {
  const { DealLayout } = await import("@/pages/Deal/DealLayout");
  return { default: DealLayout };
});

// TODO make this better
function ErrorBoundary() {
  let error = useRouteError();
  console.error(error);
  // Uncaught ReferenceError: path is not defined
  return <div>Error!</div>;
}

/* Helpers */
const workflowSidebarRoute = (basePath: string) => ({
  path: `${basePath}/:id`,
  lazy: async () => {
    if (basePath === "workflows/templates") {
      let { WorkflowTemplateOverview } = await import(
        "@/pages/Workflow/WorkflowTemplateOverview"
      );
      return {
        Component: (props: any) => <WorkflowTemplateOverview {...props} />,
      };
    }
    let { Workflow } = await import("@/pages/Workflow/Workflow");
    return { Component: (props: any) => <Workflow {...props} /> };
  },
});
const newWorkflowSidebarRoute = (basePath: string) => ({
  path: `${basePath}/:id/steps/:stepId`,
  lazy: async () => {
    if (basePath === "workflows/templates") {
      let { WorkflowTemplateOverview } = await import(
        "@/pages/Workflow/WorkflowTemplateOverview"
      );
      return {
        Component: (props: any) => <WorkflowTemplateOverview {...props} />,
      };
    }
    let { Workflow } = await import("@/pages/Workflow/Workflow");
    return { Component: (props: any) => <Workflow {...props} /> };
  },
});

const generateDocumentRoute = (
  basePath: string,
  mode: "VIEW" | "EDIT" | "SIGN"
) => ({
  path: `${basePath}/:id/${mode}`,
  lazy: async () => {
    let { Document } = await import("@/pages/Document/Document");
    return { Component: (props: any) => <Document {...props} mode={mode} /> };
  },
});

const router = createBrowserRouter([
  /*
    Public Routes (must come before protected routes to avoid auth redirect)
  */
  {
    path: "/properties/:id/share",
    errorElement: <div>Error</div>,
    lazy: async () => {
      let { PublicPropertyView } = await import(
        "@/pages/PublicProperty/PublicPropertyView"
      );
      return { Component: PublicPropertyView };
    },
  },
  {
    // Public tour-response page (REP-200 §9). No auth — the JWT in
    // ?token=... is verified by /api/public/events/:token on the server.
    path: "/events/:id/respond",
    errorElement: <div>Error</div>,
    lazy: async () => {
      let { EventRespond } = await import(
        "@/pages/EventRespond/EventRespond"
      );
      return { Component: EventRespond };
    },
  },
  {
    // Public tour-slot claim page (research/tours-painting). No auth — the
    // opaque token in the path is resolved by
    // /api/public/availability/:token on the server.
    path: "/availability/:token",
    errorElement: <div>Error</div>,
    lazy: async () => {
      let { AvailabilityClaim } = await import(
        "@/pages/AvailabilityClaim/AvailabilityClaim"
      );
      return { Component: AvailabilityClaim };
    },
  },
  {
    // Public shared-list view (REP-200 §2). Backed by
    // GET /api/lists/public/:shareToken — read-only, no auth.
    path: "/lists/share/:shareToken",
    errorElement: <div>Error</div>,
    lazy: async () => {
      let { PublicListView } = await import("@/pages/Lists/PublicListView");
      return { Component: PublicListView };
    },
  },
  {
    path: "/login",
    errorElement: <div>Error</div>,
    children: [
      {
        index: true,
        lazy: async () => {
          let { Login } = await import("@/pages/Login/Login");
          return { Component: Login };
        },
      },
      {
        path: "check",
        lazy: async () => {
          let { Check } = await import("@/pages/Login/Check");
          return { Component: Check };
        },
      },
    ],
  },
  {
    path: "/onboarding",
    errorElement: <div>Error</div>,
    children: [
      {
        path: "contact-info",
        lazy: async () => {
          let { ContactInfo } = await import("@/pages/Onboarding/ContactInfo");
          return { Component: ContactInfo };
        },
      },
      {
        path: "personal-profile",
        lazy: async () => {
          let { PersonalProfile } = await import(
            "@/pages/Onboarding/PersonalProfile"
          );
          return { Component: PersonalProfile };
        },
      },
      {
        path: "workspace",
        lazy: async () => {
          let { Workspace } = await import("@/pages/Onboarding/Workspace");
          return { Component: Workspace };
        },
      },
      {
        path: "invite/:id",
        lazy: async () => {
          let { InviteOnboarding } = await import(
            "@/pages/Onboarding/InviteOnboarding"
          );
          return { Component: InviteOnboarding };
        },
      },
    ],
  },
  {
    path: "/invite",
    errorElement: <div>Error</div>,
    children: [
      {
        path: ":id",
        lazy: async () => {
          let { Invite } = await import("@/pages/Invite/Invite");
          return { Component: Invite };
        },
      },
    ],
  },
  {
    path: "/confirmation/document",
    errorElement: <div>Error</div>,
    children: [
      {
        path: "signed",
        lazy: async () => {
          let { DocumentSigned } = await import(
            "@/pages/Confirmation/DocumentSigned"
          );
          return { Component: DocumentSigned };
        },
      },
      {
        path: "sent",
        lazy: async () => {
          let { DocumentSent } = await import(
            "@/pages/Confirmation/DocumentSent"
          );
          return { Component: DocumentSent };
        },
      },
    ],
  },
  /*
    Protected Routes, with Sidebar
  */
  {
    path: "/",
    element: <PlatformWithSidebar />,
    errorElement: <div>Error</div>,
    children: [
      ...["/", "/dashboard"].map((path) => ({
        index: true,
        path: path,
        lazy: async () => {
          let { Dashboard } = await import("@/pages/Dashboard/Dashboard");
          return { Component: Dashboard };
        },
      })),
      ...[
        {
          path: "contacts/:id?",
          lazy: async () => {
            let { Relationships } = await import(
              "@/pages/Relationships/Relationships"
            );
            return { Component: Relationships };
          },
        },
        {
          // Record System v3 — "Where your deals come from". Every deal
          // traces back to one rep agreement; this maps what each produced.
          path: "flow",
          lazy: async () => {
            let { Flow } = await import("@/pages/Flow/Flow");
            return { Component: Flow };
          },
        },
        {
          path: "deals",
          lazy: async () => {
            let { Deals } = await import("@/pages/Deals/Deals");
            return { Component: Deals };
          },
        },
        {
          // REP-211 — AI concierge. Seeded from the dashboard prompt bar via
          // ?q= / ?flow=; streams propose→confirm turns over /api/concierge.
          path: "assistant",
          lazy: async () => {
            let { Assistant } = await import("@/pages/Assistant/Assistant");
            return { Component: Assistant };
          },
        },
        {
          path: "deals/:id",
          // Own Suspense boundary (fallback={null}) so the cold-load of
          // the deal chunk doesn't flash the global `fallbackElement`
          // spinner before DealLayout's deal-shaped skeleton renders.
          // null fallback = no spinner gap → DealLayout mounts and shows
          // its header-shimmer + body-skeleton as the single first frame.
          element: (
            <Suspense fallback={null}>
              <DealLayoutLazy />
            </Suspense>
          ),
          children: [
            {
              index: true,
              lazy: async () => {
                let { DealOverview } = await import(
                  "@/pages/Deal/DealOverview"
                );
                return { Component: DealOverview };
              },
            },
            {
              path: "overview",
              lazy: async () => {
                let { DealOverview } = await import(
                  "@/pages/Deal/DealOverview"
                );
                return { Component: DealOverview };
              },
            },
            {
              path: "activity",
              lazy: async () => {
                let { DealActivity } = await import(
                  "@/pages/Deal/DealActivity"
                );
                return { Component: DealActivity };
              },
            },
            {
              // REP-208 tabs restructure — the demand rep's linked client need
              // gets a top-level "Need" tab (a reference/summary that deep-links
              // to /needs/:id). DealLayout only surfaces the tab when a need
              // exists; the pane fetches it from the rep-agreement project id.
              path: "need",
              lazy: async () => {
                let { DealNeedTab } = await import(
                  "@/pages/Deal/components/RepAgreementTabPanes"
                );
                return { Component: DealNeedTab };
              },
            },
            {
              // REP-208 tabs restructure — the supply rep's linked listing gets
              // a top-level "Listing" tab (a reference/summary that deep-links
              // to /listings/:projectId). Surfaced by DealLayout only when a
              // listing sheet exists.
              path: "listing",
              lazy: async () => {
                let { DealListingTab } = await import(
                  "@/pages/Deal/components/RepAgreementTabPanes"
                );
                return { Component: DealListingTab };
              },
            },
            {
              path: "schedule",
              lazy: async () => {
                let { DealSchedule } = await import(
                  "@/pages/Deal/DealSchedule"
                );
                return { Component: DealSchedule };
              },
            },
            {
              path: "parties",
              lazy: async () => {
                let { DealParties } = await import("@/pages/Deal/DealParties");
                return { Component: DealParties };
              },
            },
            {
              path: "settings/*",
              lazy: async () => {
                let { DealSettings } = await import(
                  "@/pages/Deal/DealSettings"
                );
                return { Component: DealSettings };
              },
            },
            // REP-205 — the listing surfaces (Inbound / Outreach / Report) are
            // NOT tabs on the deal anymore; they live on the standalone
            // /listings/:id page. The deal links out to it.
          ],
        },
        {
          path: "properties/:id",
          lazy: async () => {
            let { Property } = await import("@/pages/Property/Property");
            return { Component: Property };
          },
        },
        {
          path: "templates",
          lazy: async () => {
            let { Templates } = await import("@/pages/Templates/Templates");
            return { Component: Templates };
          },
          children: [
            {
              index: true,
              lazy: async () => {
                let { Workflows } = await import(
                  "@/pages/Templates/components/Workflows"
                );
                return { Component: Workflows };
              },
            },
            {
              path: "workflows",
              lazy: async () => {
                let { Workflows } = await import(
                  "@/pages/Templates/components/Workflows"
                );
                return { Component: Workflows };
              },
            },
            {
              path: "documents",
              lazy: async () => {
                let { Inbox } = await import("@/pages/Inbox/Inbox");
                return { Component: Inbox };
              },
              children: [
                {
                  index: true,
                  lazy: async () => {
                    let { Inbox } = await import("@/pages/Inbox/Inbox");
                    return { Component: Inbox };
                  },
                },
                {
                  path: "in-progress",
                  lazy: async () => {
                    let { Inbox } = await import("@/pages/Inbox/Inbox");
                    return { Component: Inbox };
                  },
                },
                {
                  path: "clauses",
                  lazy: async () => {
                    let { Inbox } = await import("@/pages/Inbox/Inbox");
                    return { Component: Inbox };
                  },
                },
                // Legacy routes — redirect to new paths
                {
                  path: "approved",
                  lazy: async () => {
                    let { Inbox } = await import("@/pages/Inbox/Inbox");
                    return { Component: Inbox };
                  },
                },
                {
                  path: "library",
                  lazy: async () => {
                    let { Inbox } = await import("@/pages/Inbox/Inbox");
                    return { Component: Inbox };
                  },
                },
              ],
            },
            {
              path: "clauses",
              lazy: async () => {
                let { Clauses } = await import(
                  "@/pages/Templates/components/Clauses"
                );
                return { Component: Clauses };
              },
            },
            // Old batch routes → redirect to unified documents inbox
            {
              path: "batches/upload",
              lazy: async () => {
                let { Inbox } = await import("@/pages/Inbox/Inbox");
                return { Component: Inbox };
              },
            },
            {
              path: "batches",
              lazy: async () => {
                let { Inbox } = await import("@/pages/Inbox/Inbox");
                return { Component: Inbox };
              },
            },
            {
              path: "batches/:id",
              lazy: async () => {
                let { Inbox } = await import("@/pages/Inbox/Inbox");
                return { Component: Inbox };
              },
            },
          ],
        },
        {
          path: "documents/",
          lazy: async () => {
            let { Documents } = await import("@/pages/Documents/Documents");
            return { Component: Documents };
          },
        },
        {
          path: "schedule",
          lazy: async () => {
            let { Schedule } = await import("@/pages/Schedule/Schedule");
            return { Component: Schedule };
          },
        },
        {
          // REP-206 §6 — Tours hub. Tours are first-class in the product
          // (they get a sidebar entry) but NOT a first-class data type:
          // every tour is an Event of type TOUR (REP-200), so this page
          // reads the owner-scoped /api/events?type=TOUR list and tags each
          // tour by provenance (standalone vs deal/pursuit-sourced).
          path: "tours",
          lazy: async () => {
            let { Tours } = await import("@/pages/Tours/Tours");
            return { Component: Tours };
          },
        },
        {
          path: "tasks",
          lazy: async () => {
            let { Tasks } = await import("@/pages/Tasks/Tasks");
            return { Component: Tasks };
          },
        },
        {
          path: "schedule/settings",
          lazy: async () => {
            let { ScheduleSettingsPage } = await import(
              "@/pages/Schedule/ScheduleSettingsPage"
            );
            return { Component: ScheduleSettingsPage };
          },
        },
        {
          // Focused, full-page edit experience for a client need. A freshly
          // created need also drops into this experience automatically (it's
          // a DRAFT until published); this static route lets the "Edit" action
          // and deep links reach it explicitly. Rendered by the same Need
          // component, which detects the /edit suffix.
          path: "needs/new",
          lazy: async () => {
            let { NeedEdit } = await import("@/pages/Need/NeedEdit");
            return { Component: NeedEdit };
          },
        },
        {
          // Create runs on the same page as edit — see `needs/new` above. A
          // static segment outranks `needs/:id`, so it never falls through to
          // the map.
          path: "needs/:id/edit",
          lazy: async () => {
            let { NeedEdit } = await import("@/pages/Need/NeedEdit");
            return { Component: NeedEdit };
          },
        },
        {
          // The full need page — parties, requirements, everything that does
          // not belong in a map sidebar. No longer the need's primary address
          // (see the redirect below), but still reachable and linked from the
          // map's need dashboard.
          path: "needs/:id/detail",
          lazy: async () => {
            let { Need } = await import("@/pages/Need/Need");
            return { Component: Need };
          },
        },
        {
          path: "listings",
          lazy: async () => {
            let { Listings } = await import("@/pages/Listings/Listings");
            return { Component: Listings };
          },
        },
        {
          // Optional :tab segment drives the listing's tab from the URL
          // (/listings/:id, /listings/:id/inbound, /activity, etc.). The
          // dedicated /listings/:id/push route (static) outranks this.
          path: "listings/:id/:tab?",
          lazy: async () => {
            let { Listing } = await import("@/pages/Listing/Listing");
            return { Component: Listing };
          },
        },
        {
          path: "events/:eventId",
          lazy: async () => {
            let { Event } = await import("@/pages/Event/Event");
            return { Component: Event };
          },
        },
        {
          path: "settings",
          children: [
            {
              index: true,
              element: (
                <Navigate to="/settings/personal/contact-info" replace />
              ),
            },
            {
              path: "personal/:subTab",
              lazy: async () => {
                let { SettingsPage } = await import(
                  "@/pages/Settings/SettingsPage"
                );
                return { Component: SettingsPage };
              },
            },
            {
              path: "company/:subTab",
              lazy: async () => {
                let { SettingsPage } = await import(
                  "@/pages/Settings/SettingsPage"
                );
                return { Component: SettingsPage };
              },
            },
          ],
        },
        {
          path: "search",
          lazy: async () => {
            let { Search } = await import("@/pages/Search/Search");
            return { Component: Search };
          },
        },
        {
          path: "notifications",
          lazy: async () => {
            let { Notifications } = await import(
              "@/pages/Notifications/Notifications"
            );
            return { Component: Notifications };
          },
        },
        {
          path: "components",
          lazy: async () => {
            let { Components } = await import("@/pages/Components/Components");
            return { Component: Components };
          },
        },
        // Lists — REP-200 §2 + §3. Polymorphic lists, favorites, saved
        // searches. Not a first-class sidebar entry; reach via deep link
        // or in-app surfacing (e.g. "Apply list to a deal").
        {
          path: "lists",
          lazy: async () => {
            let { Lists } = await import("@/pages/Lists/Lists");
            return { Component: Lists };
          },
        },
        workflowSidebarRoute("workflows/templates"),
        newWorkflowSidebarRoute("workflows/templates"),
      ],
    ],
  },
  /*
    Chromeless — one focused task, no navigation. The marketing builder is a
    sit-down composing surface; even the collapsed icon rail read as an exit
    competing with its own Back control.
  */
  {
    path: "/",
    element: <PlatformWithSidebar chromeless />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "listings/:id/materials",
        lazy: async () => {
          let { MarketingBuilder } = await import(
            "@/pages/PushListing/MarketingBuilder"
          );
          return { Component: MarketingBuilder };
        },
      },
    ],
  },
  /*
    Protected Routes with collapsed sidebar (documents, workflows)
  */
  {
    path: "/",
    element: <PlatformWithSidebar collapsed />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "deals/:id/tours",
        lazy: async () => {
          let { Tour } = await import("@/pages/Tour/Tour");
          return { Component: Tour };
        },
      },
      // REP-205 — full-page Push composer (PUSH IT). Sidebar-collapsed, full
      // bleed; entered from the standalone listing page (Inbound / Outreach
      // tabs), navigates back to `listings/:id` on send. `:id` = the
      // seller-rep Project id (listingProjectId).
      {
        path: "listings/:id/push",
        lazy: async () => {
          let { PushComposer } = await import(
            "@/pages/PushListing/PushComposer"
          );
          return { Component: PushComposer };
        },
      },
      // `/needs` and `/needs/:id` are the SAME surface as /properties — the
      // map — opened on the demand side. Routing rather than a query param
      // means the URL is the mode: no redirect, no param rewrite, and a
      // need's address is just /needs/:id. They live on the full-bleed branch
      // because the page owns its own h-screen layout.
      {
        path: "needs",
        lazy: async () => {
          let { Prospect } = await import("@/pages/Prospect/Prospect");
          return { Component: Prospect };
        },
      },
      {
        path: "needs/:id",
        lazy: async () => {
          let { Prospect } = await import("@/pages/Prospect/Prospect");
          return { Component: Prospect };
        },
      },
      // Properties — the map-prospecting surface (replaced the old
      // list+modal-filters page). Full-viewport: the page owns its own
      // h-screen layout, so it rides the collapsed/full-bleed branch rather
      // than the Catalyst content card.
      //
      // Honors `?addingTo=<id>` — the deep-link contract the old page
      // established, used by rep agreements, tour scheduling, and lists.
      {
        path: "properties",
        lazy: async () => {
          let { Prospect } = await import("@/pages/Prospect/Prospect");
          return { Component: Prospect };
        },
      },
      // Alias for the name this shipped under during the build.
      {
        path: "prospect",
        lazy: async () => {
          let { Prospect } = await import("@/pages/Prospect/Prospect");
          return { Component: Prospect };
        },
      },
      // Record System v3 — the full-page marketing builder. Composing a
      // launch email is a sitting-down task (write → look → pick who → send),
      // so it gets a page rather than the 620px modal it used to live in.
      // Sidebar-collapsed, full bleed; returns to `listings/:id` on send.
      // v3 — the ONE edit path for a listing. Every readiness CTA lands
      // here; nothing edits listing fields via the rep agreement any more.
      {
        path: "listings/:id/edit",
        lazy: async () => {
          let { ListingEdit } = await import("@/pages/Listing/ListingEdit");
          return { Component: ListingEdit };
        },
      },
      {
        path: "workflows/:id",
        lazy: async () => {
          let { Workflow } = await import("@/pages/Workflow/Workflow");
          return { Component: Workflow };
        },
      },
      workflowSidebarRoute("workflows"),
      newWorkflowSidebarRoute("workflows"),
      // These are all about the same, so we made a helper function for less code
      generateDocumentRoute("documents", "EDIT"),
      generateDocumentRoute("documents", "VIEW"),
      generateDocumentRoute("documents", "SIGN"),
      generateDocumentRoute("document-templates", "VIEW"),
      generateDocumentRoute("document-templates", "EDIT"),
    ],
  },
]);

export const Routes = () => {
  return (
    <RouterProvider
      router={router}
      fallbackElement={<Loader title="loading..." isFullPage />}
    />
  );
};
